/* eslint-disable @typescript-eslint/explicit-member-accessibility */

import { UserGridSettings, RowEditingMode, RowCellEditingMode, RowHeights } from './types';
import { DefaultStorage, Persistor } from '../../../../core/default-storage';
import { isUndefined } from 'lodash';
import {
  EXPANDED_ROWS,
  ROW_HEIGHTS,
  SEEDED,
  EDITING_ROWS,
  EDITING_ROW_CELLS,
  SHOW_DECIMALS,
  SHOW_0_VALUE_ACCOUNTS
} from './constants';

const defaultStorage = new DefaultStorage();

class UserGridSettingsPersistor implements Persistor<UserGridSettings> {
  public showZeroValueAccountsMap: Record<string, boolean> = {};
  private storage: Storage;

  constructor(storage: Storage = defaultStorage) {
    this.storage = storage;
    this.showZeroValueAccountsMap = this.getShowZeroValueAccountsMap();
  }

  get expandedRows() {
    const value = this.storage.getItem(EXPANDED_ROWS) || defaultStorage.getItem(EXPANDED_ROWS);

    return JSON.parse(value) as UserGridSettings['expandedRows'];
  }

  set expandedRows(value: UserGridSettings['expandedRows']) {
    this.storage.setItem(EXPANDED_ROWS, JSON.stringify(value));
  }

  get rowHeights() {
    const value = this.storage.getItem(ROW_HEIGHTS) || defaultStorage.getItem(ROW_HEIGHTS);

    return JSON.parse(value) as RowHeights;
  }

  set rowHeights(value: RowHeights) {
    this.storage.setItem(ROW_HEIGHTS, JSON.stringify(value));
  }

  get seeded() {
    const value = this.storage.getItem(SEEDED) || defaultStorage.getItem(SEEDED);

    return value === 'true';
  }

  set seeded(value: boolean) {
    this.storage.setItem(SEEDED, value.toString());
  }

  get showDecimals() {
    const value = this.storage.getItem(SHOW_DECIMALS) || defaultStorage.getItem(SHOW_DECIMALS);

    return value === 'true';
  }

  set showDecimals(value: boolean) {
    this.storage.setItem(SHOW_DECIMALS, value.toString());
  }

  getShowZeroValueAccountsMap(): Record<string, boolean> {
    const value = this.storage.getItem(SHOW_0_VALUE_ACCOUNTS) || defaultStorage.getItem(SHOW_0_VALUE_ACCOUNTS);
    try {
      const parsedValue = JSON.parse(value);

      if (parsedValue && typeof parsedValue === 'object' && !Array.isArray(parsedValue)) {
        return Object.fromEntries(
          Object.entries(parsedValue).filter(([id, entryValue]) => !!id && typeof entryValue === 'boolean')
        ) as Record<string, boolean>;
      }
    } catch {
      this.storage.setItem(SHOW_0_VALUE_ACCOUNTS, '{}');
    }
    return {};
  }

  getShowZeroValueAccounts(forecastId: string) {
    if (forecastId) {
      return this.showZeroValueAccountsMap[forecastId] !== false;
    }
    return true;
  }

  setShowZeroValueAccountsMap(value: Record<string, boolean>) {
    const nextValue = Object.fromEntries(
      Object.entries(value).filter(([id, entryValue]) => !!id && typeof entryValue === 'boolean')
    ) as Record<string, boolean>;

    this.showZeroValueAccountsMap = nextValue;
    this.storage.setItem(SHOW_0_VALUE_ACCOUNTS, JSON.stringify(nextValue));
  }

  setShowZeroValueAccounts(forecastId: string, value: boolean) {
    if (forecastId) {
      if (value === false) {
        this.showZeroValueAccountsMap[forecastId] = value;
      } else {
        delete this.showZeroValueAccountsMap[forecastId];
      }
      this.storage.setItem(SHOW_0_VALUE_ACCOUNTS, JSON.stringify(this.showZeroValueAccountsMap));
    }
  }

  get editingRows() {
    const value = this.storage.getItem(EDITING_ROWS) || defaultStorage.getItem(EDITING_ROWS);
    const parsedValue = JSON.parse(value);

    if (!Array.isArray(parsedValue)) {
      return JSON.parse(defaultStorage.getItem(EDITING_ROWS)) as RowEditingMode;
    }

    return JSON.parse(value) as RowEditingMode;
  }

  set editingRows(value: RowEditingMode) {
    this.storage.setItem(EDITING_ROWS, JSON.stringify(value));
  }

  get editingRowCells() {
    const value = this.storage.getItem(EDITING_ROW_CELLS) || defaultStorage.getItem(EDITING_ROW_CELLS);

    return JSON.parse(value) as RowCellEditingMode;
  }

  set editingRowCells(value: RowCellEditingMode) {
    this.storage.setItem(EDITING_ROW_CELLS, JSON.stringify(value));
  }

  get instance(): UserGridSettings {
    return {
      editingRowCells: this.editingRowCells,
      editingRows: this.editingRows,
      expandedRows: this.expandedRows,
      rowHeights: this.rowHeights,
      seeded: this.seeded,
      showDecimals: this.showDecimals,
      showZeroValueAccountsMap: this.showZeroValueAccountsMap
    };
  }

  public persist(payload: Partial<UserGridSettings>) {
    if (payload.expandedRows) {
      this.expandedRows = payload.expandedRows;
    }

    if (payload.rowHeights) {
      this.rowHeights = payload.rowHeights;
    }

    if (payload.seeded) {
      this.seeded = payload.seeded;
    }

    if (payload.editingRows) {
      this.editingRows = payload.editingRows;
    }

    if (payload.editingRowCells) {
      this.editingRowCells = payload.editingRowCells;
    }

    if (!isUndefined(payload.showDecimals)) {
      this.showDecimals = payload.showDecimals;
    }

    if (payload.showZeroValueAccountsMap) {
      this.setShowZeroValueAccountsMap(payload.showZeroValueAccountsMap);
    }
  }
}

/**  UserGridSettings Sigleton instance */
let _instance: UserGridSettingsPersistor | undefined;

if (!_instance) {
  _instance = new UserGridSettingsPersistor(sessionStorage);
}

const userGridSettingsPersistor = _instance;

export { userGridSettingsPersistor, UserGridSettingsPersistor };
